import {
  Box,
  Divider,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { AdjustmentTierBreakdownRow } from "gen/model/adjustmentTierBreakdownRow";
import { DetailedTierBreakdown } from "gen/model/detailedTierBreakdown";
import { DetailedTierBreakdownInfo } from "gen/model/detailedTierBreakdownInfo";
import { DetailedTierBreakdownRowGroup } from "gen/model/detailedTierBreakdownRowGroup";
import { DetailedTierBreakdownRowGroupTypeEnum } from "gen/model/detailedTierBreakdownRowGroupTypeEnum";
import { DetailedTierBreakdownRowTypeEnum } from "gen/model/detailedTierBreakdownRowTypeEnum";
import { DetailedTierBreakdownSection } from "gen/model/detailedTierBreakdownSection";
import { WaterfallDistributionData } from "gen/model/waterfallDistributionData";
import React from "react";
import { useTranslation } from "react-i18next";
import { FormSimpleMoneyInput } from "shared/components/input/text/SimpleMoneyInput";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { TableRowExpandButton } from "shared/components/table/TableRowExpandButton";
import { InfoTooltip } from "shared/components/tooltip/InfoTooltip";
import { formatPercentage } from "shared/utils/formattingUtils";
import {
  formatMoney,
  formatMoneyWithoutCurrencySymbol,
} from "shared/utils/MoneyUtils";

export interface DistributionTiersBreakdownProps {
  data: WaterfallDistributionData | undefined;
}

const TierInfoItem: React.FC<{
  title: string;
  value: string;
}> = ({ title, value }) => (
  <VStack alignItems="stretch">
    <Text textStyle="smallBold" textColor="gray.400">
      {title}
    </Text>
    <Text textStyle="small">{value}</Text>
  </VStack>
);

const TierSectionGroup: React.FC<{
  rowGroup: DetailedTierBreakdownRowGroup;
}> = ({ rowGroup }) => (
  <VStack
    alignItems="stretch"
    flex={1}
    sx={
      rowGroup.type === DetailedTierBreakdownRowGroupTypeEnum.HIGHLIGHT
        ? {
            p: "10px",
            bg: "primary.50",
            borderColor: "primary.400",
            borderWidth: 1,
            borderRadius: 4,
            mx: "-11px !important",
            "&&": {
              textStyle: "bodyBold",
            },
            "& .row-name": {
              textColor: "primary.400",
            },
            "& .row-value": {
              textColor: "primary.500",
            },
          }
        : {
            "&&": {
              textStyle: "body",
            },
          }
    }
  >
    {rowGroup.rows.map((row, rowIndex) => {
      const rowValue = (() => {
        switch (row.value_type) {
          case "MONEY":
            return formatMoney(row.value);
          case "PERCENTAGE":
            return formatPercentage(row.value);
        }
        return row.value;
      })();
      return (
        <HStack
          key={`row_${rowIndex}`}
          flex={1}
          spacing="16px"
          justifyContent="space-between"
          sx={
            row.row_type === DetailedTierBreakdownRowTypeEnum.TOTAL
              ? {
                  "&&": {
                    textStyle: "bodyBold",
                  },
                }
              : {}
          }
        >
          <div className="row-name">{row.name}</div>
          <div className="row-value">{rowValue}</div>
        </HStack>
      );
    })}
  </VStack>
);

const TierSection: React.FC<{
  section: DetailedTierBreakdownSection;
}> = ({ section }) => (
  <VStack alignItems="stretch">
    {section.header && (
      <Text textStyle="overline" textColor="primary.400">
        {section.header}
      </Text>
    )}
    <VStack alignItems="stretch" flex={1}>
      {section.row_groups.map((rowGroup, groupIndex) => (
        <React.Fragment key={groupIndex}>
          {groupIndex > 0 &&
            rowGroup.type !==
              DetailedTierBreakdownRowGroupTypeEnum.HIGHLIGHT && (
              <Divider borderColor="gray.400" />
            )}
          <TierSectionGroup key={`group_${groupIndex}`} rowGroup={rowGroup} />
        </React.Fragment>
      ))}
    </VStack>
  </VStack>
);

export const DistributionTiersBreakdown: React.FC<
  DistributionTiersBreakdownProps
> = ({ data }) => {
  const { t } = useTranslation(["distribution"]);
  if (!data) {
    return null;
  }

  return (
    <TableBox>
      <TableBoxHeader>
        <TableBoxTitle>{t("distribution:tiersBreakdown.title")}</TableBoxTitle>
      </TableBoxHeader>
      <TableBoxBody>
        <TableContainer height="100%" overflowY="auto" borderRightWidth="1px">
          <Table
            sx={{
              "& tbody td": {
                borderLeftWidth: "1px",
                borderLeftColor: "gray.200",
                bg: "gray.25",
                textAlign: "right",
              },
            }}
          >
            <Thead>
              <Tr>
                <Th>{t("distribution:tiersBreakdown.step")}</Th>
                <Th className="money" data-currency-symbol="$">
                  {t("distribution:tiersBreakdown.total")}
                </Th>
                <Th data-currency-symbol="$">
                  {t("distribution:tiersBreakdown.participating")}
                </Th>
                <Th data-currency-symbol="$">
                  {t("distribution:tiersBreakdown.carryPartners")}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Th>{t("distribution:tiersBreakdown.totalToDistribute")}</Th>
                <Td>
                  {formatMoneyWithoutCurrencySymbol(
                    data.total_distribution_amount,
                  )}
                </Td>
                <Td>{formatMoneyWithoutCurrencySymbol("0")}</Td>
                <Td>{formatMoneyWithoutCurrencySymbol("0")}</Td>
              </Tr>
              {data.detailed_distribution_data.map((tier, index) => (
                <TierBreakdown key={`tier_${index}`} tier={tier} />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </TableBoxBody>
    </TableBox>
  );
};

const TierBreakdownTooltip: React.FC<{
  info: DetailedTierBreakdownInfo;
}> = ({ info }) => {
  const { t } = useTranslation(["distribution"]);
  return (
    <InfoTooltip>
      <HStack spacing="16px">
        <TierInfoItem
          title={t("distribution:tiersBreakdown.carryPartner")}
          value={formatPercentage(info!.carry_partner)}
        />
        <TierInfoItem
          title={t("distribution:tiersBreakdown.participatingPartner")}
          value={formatPercentage(info!.participating_partner)}
        />
      </HStack>
    </InfoTooltip>
  );
};

export const TierBreakdown: React.FC<{
  tier: DetailedTierBreakdown;
}> = ({ tier }) => {
  const { isOpen, onToggle } = useDisclosure();
  const canExpand = Boolean(tier.sub_header || tier.sections?.length);
  const expandableContent = () => (
    <>
      <HStack spacing="8px" ml="-6px" align="center" minHeight="43px">
        <TableRowExpandButton isExpanded={isOpen} onClick={onToggle}>
          <Text>{tier.header}</Text>
        </TableRowExpandButton>
        {tier.info && <TierBreakdownTooltip info={tier.info} />}
      </HStack>
      {isOpen && (
        <Box ml="9px" mb="12px" maxWidth="540px">
          {tier.sub_header && (
            <Text
              textStyle="small"
              mb="12px"
              mt="-8px"
              color="gray.500"
              whiteSpace="normal"
            >
              {tier.sub_header}
            </Text>
          )}
          <VStack
            spacing="24px"
            alignItems="stretch"
            px="8px"
            // extra space if multiple sections
            py={(tier.sections?.length ?? 0) > 1 ? "8px" : undefined}
          >
            {tier.sections?.map((section, index) => (
              <TierSection key={index} section={section} />
            ))}
          </VStack>
        </Box>
      )}
    </>
  );

  const unexpandableContent = () => (
    <HStack spacing="8px" align="center" minHeight="43px" ml="9px">
      <Text>{tier.header}</Text>
      {tier.info && <TierBreakdownTooltip info={tier.info} />}
    </HStack>
  );

  return (
    <Tr>
      <Th>{canExpand ? expandableContent() : unexpandableContent()}</Th>
      <Td>
        {formatMoneyWithoutCurrencySymbol(
          tier.tier_breakdown_across_partners.total,
        )}
      </Td>
      <Td>
        {formatMoneyWithoutCurrencySymbol(
          tier.tier_breakdown_across_partners.participating_partners,
        )}
      </Td>
      <Td>
        {formatMoneyWithoutCurrencySymbol(
          tier.tier_breakdown_across_partners.carry_partners,
        )}
      </Td>
    </Tr>
  );
};

export const AdjustmentBreakdown: React.FC<{
  tier: AdjustmentTierBreakdownRow;
  isDisabled: boolean;
}> = ({ tier, isDisabled }) => (
  <Tr>
    <Th>
      <VStack align="left">
        <HStack spacing="8px" align="center">
          <Text>{tier.header}</Text>
          {tier.info && <InfoTooltip>{tier.info}</InfoTooltip>}
        </HStack>
        {tier.sub_header && (
          <Text textStyle="small" mt="-8px" color="gray.500">
            {`${tier.sub_header.sub_header}: ${formatMoney(tier.sub_header.value)}`}
          </Text>
        )}
      </VStack>
    </Th>
    <Td>{formatMoney(tier.tier_breakdown_across_partners.total!)}</Td>
    <Td>
      {formatMoney(tier.tier_breakdown_across_partners.participating_partners)}
    </Td>
    <Td>
      {tier.adjustment_type ? (
        <FormSimpleMoneyInput
          name={tier.adjustment_type}
          size="sm"
          currency="USD"
          defaultValue={tier.tier_breakdown_across_partners.carry_partners}
          isDisabled={isDisabled}
        />
      ) : (
        formatMoney(tier.tier_breakdown_across_partners.carry_partners)
      )}
    </Td>
  </Tr>
);
